import React, { FC, useEffect, useRef, useState } from 'react';
import styles from "./Stats.module.css";
import classNames from 'classnames';
import _ from 'lodash';
import { useIntersectionObserver } from 'usehooks-ts'
import { motion } from 'framer-motion';
import Odometer from 'react-odometerjs';
import { useDeviceClassification } from 'utils';

/* Prop Types */
type TCounterProps = {
    symbolName: string;
    symbol: string;
    value: string;
    dataCount: number;
    label: string;
    duration?: number;
}

enum symbolAlignment {
    'top' = 'flex-start',
    'currency' = 'center',
    'bottom' = 'flex-end',
    'center' = 'center',
}

enum symbolReverseDirection {
    'currency' = 'row-reverse',
}

type TScrollingStatsProps = {
    id: string | number;
    counters: TCounterProps[];
    duration?: string;
};

const _ScrollingStats: FC<TScrollingStatsProps> = ({
    duration = '4',
    counters,
    id
}) => {
    /* Component State */
    const [triggered, setTriggered] = useState<boolean>(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const entry = useIntersectionObserver(ref, { threshold: 0.5, root: null });
    const isVisible = entry?.isIntersecting;

    const { isDesktop } = useDeviceClassification();

    /* Animate node text conditionally */
    useEffect(() => {
        if (isVisible && !triggered) {
            document.querySelectorAll(`#${id} .odometer`).forEach((node) => {
                const countTo = node.previousElementSibling;
                setTimeout(() => {
                    node.innerHTML = countTo.getAttribute('data-count');
                }, 200)

            });

            setTriggered(true);
        }

    }, [isVisible]);

    return (
        <div style={{ '--globalOdometerDuration': `${duration}s` } as React.CSSProperties}>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: triggered ? 1 : 0 }}
                key={id}
                id={id.toString()}
                ref={ref}
                className={classNames(id, styles.counterWrapper)}>
                {counters.map((v, i) =>
                    <div key={'counter-item-' + i}
                        className="app-counter">
                        <div className={styles.counterContent}>
                            <div style={{
                                alignItems: symbolAlignment[v.symbolName],
                                flexDirection: symbolReverseDirection[v.symbolName] ? 'row-reverse' : 'row',
                                justifyContent: !isDesktop ? 'center' : symbolReverseDirection[v.symbolName] ? 'flex-end' : 'unset'
                            }}>
                                <div className="odometer-data" data-count={v.dataCount} />
                                <Odometer value={v.value} format="(ddd ddd ddd ddd),dd" />
                                <div className={`app-counter-symbol ${v.symbolName}`}>{v.symbol}</div>
                            </div>
                            <div>
                                <label>{v.label.toUpperCase()}</label>
                            </div>
                        </div>
                    </div>
                )}
            </motion.div>
        </div>
    )
};


export const ScrollingStats = React.memo(_ScrollingStats)
